import React, { useState, useEffect } from 'react';

export function AddressTabWithAPI({
  region, province, city, barangay,
  blkLot, street, postalCode, yearsOfStay, dateEmployed,
  isEditMode,
  setRegion, setProvince, setCity, setBarangay,
  setBlkLot, setStreet, setPostalCode, setYearsOfStay,
}) {
  const [regionsList, setRegionsList] = useState([]);
  const [provincesList, setProvincesList] = useState([]);
  const [citiesList, setCitiesList] = useState([]);
  const [barangaysList, setBarangaysList] = useState([]);

  const [loading, setLoading] = useState({
    region: false,
    prov: false,
    city: false,
    brgy: false,
  });

  const baseSelectClasses = `w-full rounded-md border text-sm transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 p-2.5 ${
    !isEditMode
      ? 'bg-gray-100 border-gray-200 text-gray-500 cursor-not-allowed'
      : 'bg-white border-gray-300 text-gray-900 shadow-sm'
  }`;

  const baseInputClasses = `w-full rounded-md border text-sm transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 p-2.5 ${
    !isEditMode
      ? 'bg-gray-100 border-gray-200 text-gray-500 cursor-not-allowed'
      : 'bg-white border-gray-300 text-gray-900 shadow-sm'
  }`;

  // 1. Fetch Regions
  useEffect(() => {
    setLoading(prev => ({ ...prev, region: true }));
    fetch('https://psgc.gitlab.io/api/regions/')
      .then(res => res.json())
      .then(data => {
        setRegionsList(data.sort((a, b) => a.name.localeCompare(b.name)));
        setLoading(prev => ({ ...prev, region: false }));
      })
      .catch(() => setLoading(prev => ({ ...prev, region: false })));
  }, []);

  // 2. Fetch Provinces (or clear if Region doesn't have provinces like NCR)
  useEffect(() => {
    if (!region) {
      setProvincesList([]);
      return;
    }
    const selectedReg = regionsList.find(r => r.name === region);
    if (selectedReg) {
      setLoading(prev => ({ ...prev, prov: true }));
      fetch(`https://psgc.gitlab.io/api/regions/${selectedReg.code}/provinces/`)
        .then(res => res.json())
        .then(data => {
          setProvincesList(data.sort((a, b) => a.name.localeCompare(b.name)));
          setLoading(prev => ({ ...prev, prov: false }));
        })
        .catch(() => setLoading(prev => ({ ...prev, prov: false })));
    }
  }, [region, regionsList]);

  // 3. Fetch Cities (Handles both Province level and Direct Region level like NCR)
  useEffect(() => {
    if (!region) {
      setCitiesList([]);
      return;
    }

    const selectedProv = provincesList.find(p => p.name === province);
    const selectedReg = regionsList.find(r => r.name === region);

    setLoading(prev => ({ ...prev, city: true }));

    // Fetch cities by province if a province is selected
    if (selectedProv) {
      fetch(`https://psgc.gitlab.io/api/provinces/${selectedProv.code}/cities-municipalities/`)
        .then(res => res.json())
        .then(data => {
          setCitiesList(data.sort((a, b) => a.name.localeCompare(b.name)));
          setLoading(prev => ({ ...prev, city: false }));
        })
        .catch(() => setLoading(prev => ({ ...prev, city: false })));
    } 
    // Fetch cities directly by region if there are no provinces (e.g., NCR)
    else if (selectedReg && provincesList.length === 0 && !loading.prov) {
      fetch(`https://psgc.gitlab.io/api/regions/${selectedReg.code}/cities-municipalities/`)
        .then(res => res.json())
        .then(data => {
          setCitiesList(data.sort((a, b) => a.name.localeCompare(b.name)));
          setLoading(prev => ({ ...prev, city: false }));
        })
        .catch(() => setLoading(prev => ({ ...prev, city: false })));
    } else {
      setCitiesList([]);
      setLoading(prev => ({ ...prev, city: false }));
    }
  }, [province, region, provincesList, regionsList, loading.prov]);

  // 4. Fetch Barangays
  useEffect(() => {
    if (!city) {
      setBarangaysList([]);
      return;
    }
    const selectedCity = citiesList.find(c => c.name === city);
    if (selectedCity) {
      setLoading(prev => ({ ...prev, brgy: true }));
      fetch(`https://psgc.gitlab.io/api/cities-municipalities/${selectedCity.code}/barangays/`)
        .then(res => res.json())
        .then(data => {
          setBarangaysList(data.sort((a, b) => a.name.localeCompare(b.name)));
          setLoading(prev => ({ ...prev, brgy: false }));
        })
        .catch(() => setLoading(prev => ({ ...prev, brgy: false })));
    }
  }, [city, citiesList]);

  // Automated Year of Stay Logic
  useEffect(() => {
    if (!dateEmployed) {
      setYearsOfStay(0);
      return;
    }
    const hireDate = new Date(dateEmployed);
    const today = new Date();
    let years = today.getFullYear() - hireDate.getFullYear();
    if (today < new Date(today.getFullYear(), hireDate.getMonth(), hireDate.getDate())) {
      years--;
    }
    setYearsOfStay(Math.max(0, years));
  }, [dateEmployed, setYearsOfStay]);

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white border border-gray-200 rounded-xl shadow-sm space-y-6">
      
      {/* SECTION 1: Geographic Location */}
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Region */}
          <div className="space-y-1">
            <label className="block text-xs font-medium text-gray-700">Region</label>
            <select
              className={baseSelectClasses}
              disabled={!isEditMode || loading.region}
              value={region}
              onChange={(e) => {
                setRegion(e.target.value);
                setProvince("");
                setCity("");
                setBarangay("");
              }}
            >
              <option value="">{loading.region ? "Loading..." : "Select Region"}</option>
              {regionsList.map(r => (
                <option key={r.code} value={r.name}>{r.name}</option>
              ))}
            </select>
          </div>

          {/* Province */}
          <div className="space-y-1">
            <label className="block text-xs font-medium text-gray-700">
              Province {provincesList.length === 0 && region ? "(N/A for NCR)" : ""}
            </label>
            <select
              className={baseSelectClasses}
              disabled={!isEditMode || !region || provincesList.length === 0 || loading.prov}
              value={province}
              onChange={(e) => {
                setProvince(e.target.value);
                setCity("");
                setBarangay("");
              }}
            >
              <option value="">
                {loading.prov 
                  ? "Loading..." 
                  : provincesList.length === 0 && region 
                  ? "Not Applicable" 
                  : "Select Province"}
              </option>
              {provincesList.map(p => (
                <option key={p.code} value={p.name}>{p.name}</option>
              ))}
            </select>
          </div>

          {/* City / Municipality */}
          <div className="space-y-1">
            <label className="block text-xs font-medium text-gray-700">City / Municipality</label>
            <select
              className={baseSelectClasses}
              disabled={!isEditMode || (!province && provincesList.length > 0) || !region || loading.city}
              value={city}
              onChange={(e) => {
                setCity(e.target.value);
                setBarangay("");
              }}
            >
              <option value="">{loading.city ? "Loading..." : "Select City"}</option>
              {citiesList.map(c => (
                <option key={c.code} value={c.name}>{c.name}</option>
              ))}
            </select>
          </div>

          {/* Barangay */}
          <div className="space-y-1">
            <label className="block text-xs font-medium text-gray-700">Barangay</label>
            <select
              className={baseSelectClasses}
              disabled={!isEditMode || !city || loading.brgy}
              value={barangay}
              onChange={(e) => setBarangay(e.target.value)}
            >
              <option value="">{loading.brgy ? "Loading..." : "Select Barangay"}</option>
              {barangaysList.map(b => (
                <option key={b.code} value={b.name}>{b.name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* SECTION 2: House & Street */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">
          House & Street
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1">
            <label className="block text-xs font-medium text-gray-700">Street Name</label>
            <input
              className={baseInputClasses}
              readOnly={!isEditMode}
              value={street}
              onChange={(e) => setStreet(e.target.value)}
              placeholder="e.g. Ayala Avenue"
            />
          </div>

          <div className="space-y-1">
            <label className="block text-xs font-medium text-gray-700">Blk / Lot / Building</label>
            <input
              className={baseInputClasses}
              readOnly={!isEditMode}
              value={blkLot}
              onChange={(e) => setBlkLot(e.target.value)}
              placeholder="e.g. Phase 1 Blk 2"
            />
          </div>
        </div>
      </div>

      {/* SECTION 3: Metadata */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
        <div className="space-y-1">
          <label className="block text-xs font-medium text-gray-700">Postal Code</label>
          <input
            className={baseInputClasses}
            readOnly={!isEditMode}
            value={postalCode}
            onChange={(e) => setPostalCode(e.target.value)}
            placeholder="0000"
          />
        </div>

        <div className="space-y-1">
          <label className="block text-xs font-medium text-gray-700">Years Of Stay (Calculated)</label>
          <input
            className="w-full rounded-md border text-sm p-2.5 bg-gray-100 border-gray-200 text-gray-500 cursor-not-allowed focus:outline-none"
            type="number"
            readOnly
            value={yearsOfStay}
          />
        </div>
      </div>

    </div>
  );
}